import { useFeature } from 'hooks/useFeature';
import { useTranslate } from 'hooks/useTranslations';
import { CartItems, CartItemsProps } from 'ui/components/CartItems';

import { CartSummaryAccordion } from './CartSummaryAccordion';
import { GroupedCartItems } from './GroupedCartItems';

export type CartProductSummaryProps = Omit<CartItemsProps, 'condensedView'> & {
  showEstimatedDeliveryDate?: boolean;
};

export const CartProductSummary = ({
  basketId,
  editableItems,
  preventItemLinks,
  products = [],
  showEstimatedDeliveryDate,
  errorHandling,
}: CartProductSummaryProps) => {
  const isOrderDetailsCollapsedByDefault = useFeature(
    'CHECKOUT_ACCORDION_COLLAPSED_BY_DEFAULT'
  );

  const translate = useTranslate();

  const accordionTitle = translate<'checkoutCartSummaryTitle'>(
    'checkoutCartSummaryTitle',
    {
      cartCount: products?.length as number,
    }
  );

  const headerClassNameConfig = {
    baseClassName: 'text-base sm:text-xl bg-puma-black-700 p-4 uppercase',
    expanded: 'pb-4 pl-4',
    closed: 'pb-4 pl-4 mb-6',
  };

  const bodyClassNameConfig = {
    baseClassName: 'pb-6',
  };

  return (
    <CartSummaryAccordion
      id="summary-products"
      dataTestId="summary-products"
      title={accordionTitle}
      headerClassNameConfig={headerClassNameConfig}
      bodyClassNameConfig={bodyClassNameConfig}
      expanded={!isOrderDetailsCollapsedByDefault}
    >
      <section aria-labelledby="cart-summary-products-title">
        <h3 className="sr-only" id="cart-summary-products-title">
          {translate('shoppingCart')}
        </h3>
        {showEstimatedDeliveryDate ? (
          <GroupedCartItems
            basketId={basketId}
            editableItems={editableItems}
            preventItemLinks={preventItemLinks}
            products={products}
          />
        ) : (
          <CartItems
            basketId={basketId}
            editableItems={editableItems}
            preventItemLinks={preventItemLinks}
            products={products}
            errorHandling={errorHandling}
          />
        )}
      </section>
    </CartSummaryAccordion>
  );
};
